import { apiFetch } from '../lib/apiFetch';

// ============================================
// TYPES
// ============================================

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface OverviewStats {
  totalSpend: number;
  totalConversions: number;
  totalConversionValue: number;
  roas: number | null;
  cpa: number | null;
  impressions: number;
  clicks: number;
  priorPeriod: {
    totalSpend: number;
    roas: number | null;
    cpa: number | null;
    conversions: number;
  };
}

export interface CampaignRow {
  reportDate: string;
  platform: string;
  campaignId: string;
  campaignName: string;
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
  conversionValue: number;
  cpa: number | null;
  roas: number | null;
}

export interface GeoPerformanceRow {
  bonsaibarRegion: string;
  regionKey: number | null;
  parentRegionKey: number | null;
  parentRegionName: string | null;
  totalEvents: number;
  totalCapacity: number;
  totalTicketsSold: number;
  totalTicketsAvailable: number;
  avgFillRate: number | null;
  totalAdSpend: number;
  gadsSpend: number;
  metaSpend: number;
  bookingRevenue: number;
  roasCalculated: number | null;
  totalConversions: number;
}

export interface FillRateRow {
  eventKey: number;
  eventName: string;
  workshopType: string | null;
  eventDate: string;
  localStartAt: string | null;
  venueKey: number | null;
  venueName: string | null;
  city: string | null;
  state: string | null;
  bonsaibarRegion: string | null;
  capacity: number;
  ticketsSold: number;
  ticketsAvailable: number;
  fillRate: number | null;
  pricePerPerson: number | null;
  potentialRevenue: number;
  actualRevenue: number;
}

export interface GeoMapVenue {
  venueKey: number;
  venueName: string;
  city: string | null;
  state: string | null;
  latitude: number;
  longitude: number;
  bonsaibarRegion: string | null;
  upcomingEvents: number;
  totalCapacity: number;
  totalTicketsSold: number;
  totalTicketsAvailable: number;
  avgFillRate: number | null;
}

export interface GeoMapRegionSummary {
  bonsaibarRegion: string;
  regionKey: number | null;
  totalSpend: number;
  roas: number | null;
  totalTicketsAvailable: number;
  avgFillRate: number | null;
}

export interface AlertItem {
  type: 'wasted_spend' | 'missed_opportunity' | 'declining_roas' | 'low_fill_rate';
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  region: string | null;
  campaignName: string | null;
  metric: number | null;
}

export interface GeoTrafficRow {
  region: string;
  city: string;
  sessions: number;
  totalUsers: number;
  newUsers: number;
  bounceRate: number;
  conversions: number;
}

export interface TrafficSourceRow {
  source: string;
  medium: string;
  sessions: number;
  totalUsers: number;
  conversions: number;
}

export interface LandingPageRow {
  landingPage: string;
  sessions: number;
  totalUsers: number;
  bounceRate: number;
  conversions: number;
}

export interface DailyTrendRow {
  date: string;
  platform: string;
  spend: number;
  conversionValue: number;
  conversions: number;
  impressions: number;
  clicks: number;
}

export interface PlatformBreakdown {
  platform: string;
  spend: number;
  conversions: number;
  conversionValue: number;
  impressions: number;
  clicks: number;
  roas: number | null;
}

export interface Pagination {
  limit: number;
  offset: number;
  total: number;
}

// ============================================
// API FUNCTIONS
// ============================================

function buildQuery(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [key, val] of Object.entries(params)) {
    if (val !== undefined && val !== '') q.set(key, String(val));
  }
  return q.toString();
}

export async function fetchOverview(range?: DateRange): Promise<{
  stats: OverviewStats;
  dailyTrend: DailyTrendRow[];
  platformBreakdown: PlatformBreakdown[];
  dateRange: DateRange;
}> {
  const query = buildQuery({
    startDate: range?.startDate,
    endDate: range?.endDate,
  });
  const res = await apiFetch(`/api/v1/marketing/overview?${query}`);
  if (!res.ok) throw new Error('Failed to fetch marketing overview');
  return res.json();
}

export async function fetchCampaigns(
  range?: DateRange,
  platform?: string,
  limit?: number,
  offset?: number,
): Promise<{
  campaigns: CampaignRow[];
  dateRange: DateRange;
  pagination: Pagination;
}> {
  const query = buildQuery({
    startDate: range?.startDate,
    endDate: range?.endDate,
    platform,
    limit,
    offset,
  });
  const res = await apiFetch(`/api/v1/marketing/campaigns?${query}`);
  if (!res.ok) throw new Error('Failed to fetch campaigns');
  return res.json();
}

export async function fetchCampaignTrend(
  campaignId: string,
  range?: DateRange,
): Promise<{
  trend: CampaignRow[];
  dateRange: DateRange;
}> {
  const query = buildQuery({
    startDate: range?.startDate,
    endDate: range?.endDate,
  });
  const res = await apiFetch(`/api/v1/marketing/campaigns/${campaignId}/trend?${query}`);
  if (!res.ok) throw new Error('Failed to fetch campaign trend');
  return res.json();
}

export async function fetchGeoPerformance(range?: DateRange): Promise<{
  regions: GeoPerformanceRow[];
  dateRange: DateRange;
}> {
  const query = buildQuery({
    startDate: range?.startDate,
    endDate: range?.endDate,
  });
  const res = await apiFetch(`/api/v1/marketing/geo-performance?${query}`);
  if (!res.ok) throw new Error('Failed to fetch geo performance');
  return res.json();
}

export async function fetchGeoMap(startDate?: string, endDate?: string): Promise<{
  venues: GeoMapVenue[];
  regionSummaries: GeoMapRegionSummary[];
}> {
  const query = buildQuery({ startDate, endDate });
  const res = await apiFetch(`/api/v1/marketing/geo-map?${query}`);
  if (!res.ok) throw new Error('Failed to fetch geo map data');
  return res.json();
}

export async function fetchFillRates(
  range?: DateRange,
  regionId?: string,
  state?: string,
  limit?: number,
  offset?: number,
): Promise<{
  events: FillRateRow[];
  dateRange: DateRange;
  pagination: Pagination;
}> {
  const query = buildQuery({
    startDate: range?.startDate,
    endDate: range?.endDate,
    regionId,
    state,
    limit,
    offset,
  });
  const res = await apiFetch(`/api/v1/marketing/fill-rates?${query}`);
  if (!res.ok) throw new Error('Failed to fetch fill rates');
  return res.json();
}

export async function fetchAlerts(): Promise<{
  alerts: AlertItem[];
}> {
  const res = await apiFetch('/api/v1/marketing/alerts');
  if (!res.ok) throw new Error('Failed to fetch alerts');
  return res.json();
}

export async function fetchTraffic(range?: DateRange): Promise<{
  available: boolean;
  message?: string;
  geo: GeoTrafficRow[];
  sources: TrafficSourceRow[];
  landingPages: LandingPageRow[];
  dateRange: DateRange;
}> {
  const query = buildQuery({
    startDate: range?.startDate,
    endDate: range?.endDate,
  });
  const res = await apiFetch(`/api/v1/marketing/traffic?${query}`);
  if (!res.ok) throw new Error('Failed to fetch traffic data');
  return res.json();
}
